import { ImagePrompt } from "@/types/entity";

/**
 * 本地缓存操作
 * 收敛对localStorage的操作和kv的类型，避免类型错误和key的冲突
 */

type ValueOf<T> = T[keyof T];

interface CacheKey {
  imgPrompts: ImagePrompt[];
  showCreateTraitGuide: boolean;
}
interface LocalCache<T extends ValueOf<CacheKey>> {
  data: T;
  timestamp: number; // 写入ms时间戳
}

/**
 * 从localStorage中获取数据，如果不存在则调用getter获取数据并写入localStorage
 * @param key
 * @param timeout
 * @param getter
 */
export const getOrRefresh = async <
  T extends keyof CacheKey,
  K extends CacheKey[T]
>(
  key: T,
  timeout: number,
  getter: () => Promise<K>
): Promise<K> => {
  const strVal = localStorage.getItem(key);
  let cache: LocalCache<K> | null = null;
  if (strVal) {
    cache = JSON.parse(strVal);
  }
  if (cache && !isExpired(cache, timeout)) {
    return cache.data;
  }
  const data = await getter();
  const newCache: LocalCache<K> = {
    data,
    timestamp: Date.now(),
  };
  localStorage.setItem(key, JSON.stringify(newCache));
  return data;
};

function isExpired(data: LocalCache<any>, timeout: number) {
  // 2023-11-22 20:22:23
  if (!data.timestamp || data.timestamp < 1700655743392) {
    return true;
  }
  return Date.now() - data.timestamp > timeout;
}

/**
 * 从localStorage中获取数据{}
 * 存在：返回数据
 * 不存在：将valIfAbsent写入localStorage并返回null
 * @param key
 * @param valIfAbsent
 */
export const getOrSet = <T extends keyof CacheKey, K extends CacheKey[T]>(
  key: T,
  valIfAbsent: K
): K | null => {
  const strVal = localStorage.getItem(key);
  if (strVal === null) {
    const newCache: LocalCache<K> = {
      data: valIfAbsent,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(newCache));
    return null;
  } else {
    return JSON.parse(strVal).data as K;
  }
};
